import React, { useState, useEffect } from 'react';
import { X, Trash2, Plus, Calendar, DollarSign, Layers } from 'lucide-react';
import toast from 'react-hot-toast';

const FeeStructureModal = ({ structure, onClose, onSubmit, allClasses, currentAcademicYear }) => {
    const [formData, setFormData] = useState({
        name: '',
        academicYear: currentAcademicYear,
        description: '',
        categories: [{ name: '', amount: '', dueDate: '', isMandatory: true }],
        applicableClassIds: [],
        applicableSectionIds: [],
        fineRules: [],
        isPartialPaymentAvailable: true
    });

    useEffect(() => {
        if (structure) {
            // Avoid infinite loop by checking if state already matches structure
            if (formData.name !== (structure.name || '')) {
                // eslint-disable-next-line
                setFormData({
                    name: structure.name || '',
                    academicYear: structure.academicYear || currentAcademicYear,
                    description: structure.description || '',
                    categories: structure.categories?.map(c => ({
                        ...c,
                        dueDate: c.dueDate ? c.dueDate.split('T')[0] : ''
                    })) || [],
                    applicableClassIds: structure.applicableClassIds || [],
                    applicableSectionIds: structure.applicableSectionIds || structure.applicableCourseIds || [], // Handle both for compatibility
                    fineRules: structure.fineRules || [],
                    isPartialPaymentAvailable: structure.isPartialPaymentAvailable !== undefined ? structure.isPartialPaymentAvailable : true
                });
            }
        }
    }, [structure, currentAcademicYear]);

    const handleChange = (field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));
    };

    const preventArrowKeys = (e) => {
        if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
            e.preventDefault();
        }
    };

    const handleCategoryChange = (index, field, value) => {
        const newCategories = [...formData.categories];
        newCategories[index] = { ...newCategories[index], [field]: value };
        setFormData(prev => ({ ...prev, categories: newCategories }));
    };

    const addCategory = () => {
        setFormData(prev => ({
            ...prev,
            categories: [...prev.categories, { name: '', amount: '', dueDate: '', isMandatory: true }]
        }));
    };

    const removeCategory = (index) => {
        setFormData(prev => ({
            ...prev,
            categories: prev.categories.filter((_, i) => i !== index)
        }));
    };
    
    const handleClassToggle = (classId) => {
        setFormData(prev => {
            const ids = prev.applicableClassIds || [];
            if (ids.includes(classId)) {
                return { ...prev, applicableClassIds: ids.filter(id => id !== classId) };
            } else {
                return { ...prev, applicableClassIds: [...ids, classId] };
            }
        });
    };

    const handleSelectAllClasses = () => {
        if (formData.applicableClassIds.length === allClasses.length) {
            setFormData(prev => ({ ...prev, applicableClassIds: [] }));
        } else {
            setFormData(prev => ({ ...prev, applicableClassIds: allClasses.map(c => c._id) }));
        }
    };

    const validateForm = () => {
        if (!formData.name.trim()) {
            toast.error("Structure Name is required");
            return false;
        }
        if (formData.categories.length === 0) {
            toast.error("At least one fee category is required");
            return false;
        }
        for (const cat of formData.categories) {
            if (!cat.name.trim()) {
                toast.error("All categories must have a name");
                return false;
            }
            if (!cat.amount || Number(cat.amount) <= 0) {
                toast.error("All categories must have a valid amount");
                return false;
            }
            if (!cat.dueDate) {
                toast.error("All categories must have a due date");
                return false;
            }
        }
        return true;
    };

    const handleSubmit = () => {
        if (validateForm()) {
            const finalData = {
                ...formData,
                categories: formData.categories.map(cat => ({
                    ...cat,
                    dueDate: cat.dueDate ? new Date(cat.dueDate).toISOString() : null
                }))
            };
            onSubmit(finalData);
        }
    };

    const totalAmount = formData.categories.reduce((sum, cat) => sum + (Number(cat.amount) || 0), 0);

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-gray-600 bg-opacity-75 backdrop-blur-sm">
            <div className="bg-white rounded shadow-xl max-w-2xl w-full max-h-[90vh] flex flex-col">
                <div className="flex items-center justify-between px-3 py-3 border-b border-gray-200 bg-gray-50 rounded-t">
                    <div>
                        <h3 className="text-base font-bold text-gray-900">
                            {structure ? 'Edit Fee Structure' : 'Create Fee Structure'}
                        </h3>
                        <p className="mt-0.5 text-xs text-gray-500">
                            Define fee details and assign to classes
                        </p>
                    </div>
                    <button 
                        onClick={onClose}
                        className="p-1.5 text-gray-400 transition-colors rounded-full hover:text-gray-600 hover:bg-gray-200"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>
                
                <div className="flex-1 p-4 overflow-y-auto">
                    <div className="space-y-4">
                        {/* Basic Info Section */}
                        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                            <div className="col-span-1">
                                <label className="block mb-1 text-xs font-bold text-gray-700">
                                    Structure Name <span className="text-red-500">*</span>
                                </label>
                                <div className="relative">
                                    <Layers className="absolute left-2.5 top-1/2 transform -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
                                    <input 
                                        type="text" 
                                        value={formData.name} 
                                        onChange={e => handleChange('name', e.target.value)}
                                        className="w-full pl-8 px-3 py-1.5 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 transition-colors"
                                        placeholder="e.g. Annual Fee 2025-26"
                                        required
                                    />
                                </div>
                            </div>
                            <div className="col-span-1">
                                <label className="block mb-1 text-xs font-bold text-gray-700">
                                    Academic Year
                                </label>
                                <div className="relative">
                                    <Calendar className="absolute left-2.5 top-1/2 transform -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
                                    <input 
                                        type="text" 
                                        value={formData.academicYear} 
                                        onChange={e => handleChange('academicYear', e.target.value)}
                                        className="w-full pl-8 px-3 py-1.5 text-xs bg-gray-50 border border-gray-300 rounded text-gray-500 cursor-not-allowed"
                                        readOnly
                                    />
                                </div>
                            </div>
                            <div className="md:col-span-2">
                                <label className="block mb-1 text-xs font-bold text-gray-700">
                                    Description
                                </label>
                                <textarea 
                                    value={formData.description} 
                                    onChange={e => handleChange('description', e.target.value)}
                                    rows={2}
                                    className="w-full px-3 py-1.5 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 transition-colors resize-none"
                                    placeholder="Optional description for this fee structure..."
                                />
                            </div>
                             <div className="md:col-span-2">
                                <label className="flex items-center space-x-2 cursor-pointer">
                                    <input
                                        type="checkbox"
                                        checked={formData.isPartialPaymentAvailable}
                                        onChange={e => handleChange('isPartialPaymentAvailable', e.target.checked)}
                                        className="w-4 h-4 border-gray-300 rounded"
                                        style={{ accentColor: 'var(--primary-color, #4f46e5)' }}
                                    />
                                    <span className="text-xs font-bold text-gray-700">Partial Payment Available</span>
                                </label>
                                <p className="text-[10px] text-gray-500 mt-0.5 ml-6">
                                    If unchecked, students must pay the full amount for selected categories.
                                </p>
                            </div>
                        </div>

                        {/* Applicable Classes Section */}
                        <div>
                            <div className="flex items-center justify-between mb-2">
                                <label className="text-xs font-bold text-gray-700">
                                    Applicable Classes
                                </label>
                                <button 
                                    onClick={handleSelectAllClasses}
                                    className="text-[10px] font-medium hover:underline"
                                    style={{ color: 'var(--primary-color, #4f46e5)' }}
                                >
                                    {formData.applicableClassIds.length === allClasses.length ? 'Deselect All' : 'Select All'}
                                </button>
                            </div>
                            <div className="p-3 border border-gray-200 rounded bg-gray-50">
                                <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5">
                                    {allClasses.map(cls => (
                                        <label 
                                            key={cls._id} 
                                            className={`
                                                relative flex items-center p-2 border rounded cursor-pointer transition-all
                                                ${formData.applicableClassIds.includes(cls._id) 
                                                    ? 'bg-[var(--primary-bg-color)] border-[var(--primary-border-color)] ring-1 ring-[var(--primary-ring-color)]' 
                                                    : 'bg-white border-gray-200 hover:border-gray-300 hover:shadow-sm'}
                                            `}
                                        >
                                            <input 
                                                type="checkbox"
                                                checked={formData.applicableClassIds.includes(cls._id)}
                                                onChange={() => handleClassToggle(cls._id)}
                                                className="w-3.5 h-3.5 border-gray-300 rounded"
                                                style={{ accentColor: 'var(--primary-color, #4f46e5)' }}
                                            />
                                            <span className="ml-1.5 text-xs font-medium text-gray-700 truncate">
                                                {cls.unitName}
                                            </span>
                                        </label>
                                    ))}
                                </div>
                                {allClasses.length === 0 && (
                                    <p className="text-xs text-center text-gray-500">No classes available.</p>
                                )}
                            </div>
                        </div>

                        {/* Fee Categories Section */}
                        <div>
                            <div className="flex items-center justify-between mb-3">
                                <div>
                                    <h4 className="text-xs font-bold text-gray-900">Fee Breakdown</h4>
                                    <p className="text-[10px] text-gray-500">Add components like Tuition, Transport, etc.</p>
                                </div>
                                <div className="flex items-center gap-2">
                                    <div className="text-xs font-medium text-gray-700">
                                        Total: <span className="font-bold text-sm" style={{ color: 'var(--primary-color, #4f46e5)' }}>₹{totalAmount}</span>
                                    </div>
                                    <button 
                                        onClick={addCategory} 
                                        className="btn-primary text-xs flex items-center gap-0.5"
                                    >
                                        <Plus className="h-3.5 w-3.5" /> Add Category
                                    </button>
                                </div>
                            </div>
                            
                            <div className="space-y-2">
                                {formData.categories.map((cat, idx) => (
                                    <div key={idx} className="flex flex-col md:flex-row items-start md:items-center gap-2 p-2 border border-gray-200 rounded bg-gray-50 hover:bg-white hover:shadow-sm transition-all group">
                                        <div className="flex-1 w-full md:w-auto">
                                            <label className="block text-[10px] font-medium text-gray-500 mb-0.5 md:hidden">Category Name</label>
                                            <input 
                                                type="text" 
                                                placeholder="Category Name (e.g. Tuition)"
                                                value={cat.name}
                                                onChange={e => handleCategoryChange(idx, 'name', e.target.value)}
                                                className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded focus:ring-1 focus:border-indigo-500 focus:ring-indigo-500 transition-colors"
                                                required
                                            />
                                        </div>
                                        <div className="w-full md:w-32">
                                            <label className="block text-[10px] font-medium text-gray-500 mb-0.5 md:hidden">Amount</label>
                                            <div className="relative">
                                                <span className="absolute text-gray-500 transform -translate-y-1/2 left-2 top-1/2 text-xs">₹</span>
                                                <input 
                                                    type="number" 
                                                    placeholder="0"
                                                    value={cat.amount}
                                                    onKeyDown={preventArrowKeys}
                                                    onChange={e => handleCategoryChange(idx, 'amount', e.target.value)}
                                                    className="w-full pl-5 px-2 py-1.5 text-xs border border-gray-300 rounded focus:ring-1 focus:border-indigo-500 focus:ring-indigo-500 transition-colors"
                                                    required
                                                    min="0"
                                                />
                                            </div>
                                        </div>
                                        <div className="w-full md:w-32">
                                            <label className="block text-[10px] font-medium text-gray-500 mb-0.5 md:hidden">Due Date</label>
                                            <input 
                                                type="date" 
                                                value={cat.dueDate}
                                                onChange={e => handleCategoryChange(idx, 'dueDate', e.target.value)}
                                                className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded focus:ring-1 focus:border-indigo-500 focus:ring-indigo-500 transition-colors"
                                                required
                                            />
                                        </div>
                                        <div className="flex items-center justify-end w-full md:w-auto mt-1 md:mt-0">
                                            <button 
                                                onClick={() => removeCategory(idx)} 
                                                className="p-1.5 text-gray-400 transition-colors rounded hover:text-red-600 hover:bg-red-50"
                                                title="Remove category"
                                            >
                                                <Trash2 className="w-3.5 h-3.5" />
                                            </button>
                                        </div>
                                    </div>
                                ))}
                                {formData.categories.length === 0 && (
                                    <div className="text-center py-6 border-2 border-dashed border-gray-200 rounded text-xs text-gray-500">
                                        No categories added yet. Click "Add Category" to start.
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
                
                <div className="flex items-center justify-end gap-2 px-3 py-3 border-t border-gray-200 bg-gray-50 rounded-b">
                    <button 
                        onClick={onClose} 
                        className="px-3 py-1.5 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50 focus:outline-none focus:ring-1 focus:ring-offset-1 focus:ring-gray-500"
                    >
                        Cancel
                    </button>
                    <button 
                        onClick={handleSubmit} 
                        className="btn-primary text-xs shadow-sm"
                    >
                        {structure ? 'Update Structure' : 'Create Structure'}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default FeeStructureModal;
